import {HttpParams} from '@angular/common/http';
import {ChangeDetectionStrategy, Component, OnInit, TemplateRef, ViewChild,} from '@angular/core';
import {FormGroup} from '@angular/forms';
import {ActivatedRoute, Params} from '@angular/router';
import {FormlyFormOptions} from '@ngx-formly/core';
import {BreadcrumbDto, DamCatalogPlain, DamElasticDTO, DamItemVersion, FileService, PageApiResponse, Picture, Role} from 'compob2b-libs';
import {BehaviorSubject, Observable, of} from 'rxjs';
import {catchError, map, pairwise, switchMap, take, takeUntil, tap} from 'rxjs/operators';
import {translations} from '../../../../../environments/translations.ru';
import {CrudConfig} from '../../../../admin-shared/interfaces/crud.config';
import {RelatedEntity} from '../../../../admin-shared/interfaces/related.entity';
import {FormConfigService} from '../../../../admin-shared/services/form-config.service';
import {DamShareComponent} from '../../dam-share/dam-share.component';
import {DamUnitItemsComponent} from '../../dam-unit-items/dam-unit-items.component';
import {DamService} from '../../dam.service';
import {DamFilterService} from "../../dam-filter.service";
import {ViewportScroller} from "@angular/common";
import {ListComponent} from "../../../../admin-shared/crud-components/list/list.component";
import {NbWindowService} from "@nebular/theme";
import {DAM_CATALOG_BATCH_FORM} from "./dam-catalog-batch-form";

export interface DamItemVersionTypeCountWSelected {
  count: number;
  typeOfFile: string;
  selected: boolean;
}

@Component({
  templateUrl: './dam-catalog.component.html',
  styleUrls: ['./dam-catalog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DamCatalogComponent extends ListComponent<DamElasticDTO> implements OnInit {
  @ViewChild('filtersTpl', {static: true}) filtersTpl: TemplateRef<any>;

  config: CrudConfig = {
    uri: '/dam/files',
    columns: [],
    defaultSortColumn: 'header',
    defaultSortDirection: 'asc',
    title: translations.files,
    checkboxes: true
  };

  isShowList = (localStorage.getItem('damView') || 'list') == 'list';

  isFiltersOpen = false;
  formFilter = new FormGroup({});
  catalogBreadCrumbs: Observable<BreadcrumbDto[]>;
  catalogId: number;

  createFolderName: string = '';
  damElasticDTO: DamElasticDTO = null;
  productShow = false;

  damItemVersionWithDocumentInfo: DamItemVersion[];
  damItemVersionWithDocumentInfoExtensions: string[];
  damItemVersionWithDocumentInfoExtensionsCount: DamItemVersionTypeCountWSelected[];

  selected$ = new BehaviorSubject(new Set());

  formBatch = new FormGroup({});
  configBatch: CrudConfig;
  optionsBatch: FormlyFormOptions = {};
  modelBatch: any = {};
  allRoles: Role[];
  selectedElements: DamElasticDTO[] = [];

  constructor(
    protected readonly formsConfig: FormConfigService,
    protected readonly windowService: NbWindowService,
    protected readonly fileService: FileService,
    protected readonly activatedRoute: ActivatedRoute,
    protected readonly damService: DamService,
    protected readonly filter: DamFilterService,
    protected readonly scroller: ViewportScroller,
  ) {
    super(formsConfig, windowService, fileService);
  }

  protected filterData(): Observable<DamElasticDTO[]> {
    if (!this.loading) {
      this.loading = true;
      const params = this.route.snapshot.queryParams;

      this.updatePagerFromUri();
      const uri = this.getListURI(this.config.uri + '/' + (params.catalog_id || 0));

      this.compoApiService.getWithParams<PageApiResponse<DamElasticDTO[]>>(uri, new HttpParams({fromObject: params}))
        .pipe(
          take(1),
          catchError(err => this.handleError(err)),
          tap(response => {
            this.handleResponse(response.data);
            this.data = of(response.data.content);
            this.cdr.detectChanges();
          }),
        )
        .subscribe();
    }

    return this.data;
  }

  ngOnInit(): void {
    super.setConfig(this.config);
    this.configure();

    this.route.queryParamMap.pipe(
      tap(params => {
        this.paramsProcess(params);
        this.filterData();

        let catalogId = 1;
        if (Number(params.get('catalog_id')) > 1) {
          catalogId = Number(params.get('catalog_id'));
        }
        this.catalogBreadCrumbs = this.damService.getCatalogBreadCrumbs(catalogId)
          .pipe(
            tap(items => {
              if (items.length) {
                this.title = items.length ? items[items.length - 1].header : translations.files;
              }
              else {
                this.title = translations.files;
              }

              this.storeService.setTitle(this.title);
            })
          );
        this.catalogId = Number(params.get('catalog_id'));
      }),
      switchMap(params1 => {
        const params = this.paramsToHttp(params1)
          .set('templateId', this.environment.damTemplateId);

        return this.filter.getProductFilter(this.config.uri, this.catalogId, params);
      }),
      tap(_ => {
        this.loading = false;
        this.cdr.detectChanges();
      }),
      takeUntil(this.unsubscribe)
    ).subscribe();

    this.subOnRouteChanges();

    const allRoles = this.compoApiService.get<Role[]>('/dam-menu/list-roles-for-batch/')
      .pipe(map(response => response.data), tap(data => this.allRoles = data));

    const relatedEntities: RelatedEntity[] = [
      {multipleEntity: allRoles, multipleField: 'rolesName'}
    ];

    this.configBatch = {
      uri: '/dam/',
      columns: DAM_CATALOG_BATCH_FORM,
      title: translations.operations,
      relatedEntity: relatedEntities,
      absolutePath: ''
    };
    this.formsConfig.setConfig(this.configBatch.columns).formly();

    relatedEntities.forEach(item => this.formsConfig.addMultipleOptions(item.multipleField, item.multipleEntity));

    this.formsConfig.setConfig(this.configBatch.columns).formly();
  }

  showProductBlocks(type: string) {
    this.closeSidebar();
    this.isShowList = type === 'list';
    localStorage.setItem('damView', type);
  }

  getImageUri(picture: Picture, width: number = 96) {
    return picture === null ? null : this.fileService.getImageSrc(picture.name + '.' + picture.type, width);
  }

  changeSort($event) {
    console.log($event);
    const sortData = $event.split('_');
    const queryParams: Params = {sort: sortData[0], direction: sortData[1]};
    this.router.navigate(
      ['.'],
      {
        relativeTo: this.activatedRoute,
        queryParams,
        queryParamsHandling: 'merge'
      }
    );
  }

  shareDamItems(items: DamElasticDTO[]) {
    this.windowService.open(DamShareComponent, {
      context: {
        damItems: items.map(obj => obj.id),
        damCatalog: null,
        shareType: 0
      }
    });
  }

  downloadDamItems() {
    this.fileService.downloadPostUrl('/dam-download/dam-items/', {
        damItemsIds: Array.from(this.selected$.value)
      })
      .pipe(take(1))
      .subscribe(response => {
        FileService.saveAs(response, 'download.zip');
      });
  }

  unitDamItems() {
    if (this.selectedElements.length > 1) {
      this.windowService.open(DamUnitItemsComponent, {
        title: 'Объединить элементы',
        context: {
          damItems: this.selectedElements
        }
      });
    }
  }

  enableDamItems() {
    this.confirm('Активировать запись?', this.compoApiService
      .post('/dam/items/set-enabled', {damItemsIds: Array.from(this.selected$.value)})
      .pipe(
        take(1),
        catchError(err => this.handleError(err)),
        tap(_ => {
          this.successToast('Запись активирована');
          this.cdr.detectChanges();
        }),
      )
    );
  }

  approveDamItems() {
    this.confirm('Одобрить запись?', this.compoApiService
      .post('/dam/items/set-approved', {damItemsIds: Array.from(this.selected$.value)})
      .pipe(
        take(1),
        catchError(err => this.handleError(err)),
        tap(_ => {
          this.successToast('Запись одобрена');
          this.cdr.detectChanges();
        }),
      )
    );
  }

  deleteDamItems() {
    this.confirm('Удалить запись?', this.compoApiService
      .post('/dam/items/set-deleted', {damItemsIds: Array.from(this.selected$.value)})
      .pipe(
        take(1),
        catchError(err => this.handleError(err)),
        tap(_ => {
          this.successToast(translations.hasDeleted);
          this.cdr.detectChanges();
        }),
      )
    );
  }

  batchSubmit(windowRef) {
    if (this.formBatch.invalid) {
      return false;
    }

    if (this.selected.size === 0 && this.modelBatch.forAll === false) {
      this.warningToast(translations.batchUpdateError);
      return false;
    }

    if (this.modelBatch.operation === 'none') {
      this.warningToast(translations.selectOperations);
      return false;
    }

    const tagsSelected = {};
    const changeAclModeSelected = {};//changeAcl
    const featureEditMulti = [];
    if (this.modelBatch.operation === 'tags' && Object.keys(this.modelBatch.tags).length > 0) {
      Object.keys(this.modelBatch.tags).forEach(key => {
        const value = this.modelBatch.tags[key];
        if (value === true) {
          tagsSelected[key] = value;
        }
      });
    }
    if (this.modelBatch.operation === 'tags' && Object.keys(tagsSelected).length === 0) {
      this.errorToast(translations.selectTag);
      return false;
    }

    if (this.modelBatch.operation === 'changeAcl' && Object.keys(this.modelBatch.changeAclMode).length > 0) {
      Object.keys(this.modelBatch.changeAclMode).forEach(key => {
        const value = this.modelBatch.changeAclMode[key];
        if (value === true) {
          changeAclModeSelected[key] = value;
        }
      });
    }
    if (this.modelBatch.operation === 'tags' && Object.keys(changeAclModeSelected).length === 0) {
      this.errorToast(translations.selectChangeAclModeSelected);
      return false;
    }

    // if (this.modelBatch.operation === 'changeAcl') {
    //   for (const sourceFeatureId in this.modelBatch.templateMapping) {
    //     const featureMapping: TemplateFeatureDestMapping = this.modelBatch.templateMapping[sourceFeatureId];
    //     const itemFeatureMapping: TemplateFeatureMapping = {sourceFeatureId: parseInt(sourceFeatureId, 10), ...featureMapping};
    //     templateMapping.push(itemFeatureMapping);
    //   }
    // }

    const paramsForBatch = {
      operation: this.modelBatch.operation,
      forAll: this.modelBatch.forAll,
      catalogId: this.modelBatch.catalogId,
      catalogs: this.modelBatch.catalogs,
      changeMode: this.modelBatch.changeMode,
      changeAclMode: changeAclModeSelected,
      tags: tagsSelected,
      deleteAction: this.modelBatch.deleteAction,
      brandId: this.modelBatch.brandId,
      manufacturerId: this.modelBatch.manufacturerId,
      operationProductParams: this.modelBatch.operationProductParams,
      operationProductParamsInput: this.modelBatch.operationProductParamsInput,
      unitId: this.modelBatch.unitId,
      rolesName: this.modelBatch.rolesName,
      productGroupId: this.modelBatch.productGroupId,
      templateId: this.modelBatch.templateId,
      featuresId: this.modelBatch.featuresId,
      sourceValue: this.modelBatch.sourceValue,
      featureEditMulti,
      fieldConfigUpdate: this.modelBatch.fieldConfigUpdate,
      fieldConfigReplace: this.modelBatch.fieldConfigReplace === true,
      selected: Array.from(this.selected$.value),
      newTemplateId: this.modelBatch.newTemplateId,
    };

    const params: any = this.activatedRoute.snapshot.queryParams;
    const queryParamsString = new HttpParams({fromObject: params}).toString();
    const uri: string = '/dam/batch/' + this.catalogId + '?' + queryParamsString;

    this.confirm(translations.batchCreate,
      this.compoApiService
        .post(uri, paramsForBatch)
        .pipe(
          take(1),
          catchError(err => this.handleError(err)),
          tap(_ => {
            windowRef.close();
            this.successToast(translations.jobExecuted);
            this.cdr.detectChanges();
          })
        )
    );
  }

  elementClickEventHandler(element: DamElasticDTO) {
    this.damElasticDTO = element;

    this.compoApiService.get<DamItemVersion[]>('/dam/' + element.id + '/versions').pipe(take(1)).subscribe(response => {

      this.damItemVersionWithDocumentInfoExtensions = [];
      this.damItemVersionWithDocumentInfoExtensionsCount = [];
      this.damItemVersionWithDocumentInfo = response.data;

      this.damItemVersionWithDocumentInfo.forEach(damItemVersion => {
        this.damItemVersionWithDocumentInfoExtensions.push(damItemVersion.mimeType);
      });
      this.damItemVersionWithDocumentInfoExtensions = this.damItemVersionWithDocumentInfoExtensions.filter(this.onlyUnique);
      this.damItemVersionWithDocumentInfoExtensions.forEach(oneExtension => {
        const itms = this.damItemVersionWithDocumentInfo.filter(version => version.mimeType == oneExtension);
        let damItemCountType = {} as DamItemVersionTypeCountWSelected;
        damItemCountType.count = itms.length;
        damItemCountType.typeOfFile = oneExtension;
        this.damItemVersionWithDocumentInfoExtensionsCount.push(damItemCountType);
      });

      this.productShow = true;
      this.cdr.detectChanges();
    });
  }

  onlyUnique(value, index, array) {
    return array.indexOf(value) === index;
  }

  closeSidebar() {
    this.productShow = false;
  }

  onElementSelect(selectedItem: DamElasticDTO) {
    if (selectedItem?.selected) {
      this.selectedElements.push(selectedItem);
    }
    else {
      this.selectedElements = this.selectedElements.filter(item => item?.id !== selectedItem?.id);
    }
    console.log('selected SET', this.selected$.value);
    console.log('selectedElements', this.selectedElements);
    this.cdr.detectChanges();
  }

  createFastFolder() {
    this.compoApiService.post<any, DamCatalogPlain>('/dam-menu/create-folder-fast', {
      parentId: this.catalogId,
      header: this.createFolderName
    }).pipe(take(1), catchError(err => this.handleError(err))).subscribe(response => {
      window.location.href = '/cabinet/dam?catalog_id=' + response.data.id;
    });
  }

  isRootCatalog() {
    return this.catalogId < 1;
  }

  subOnRouteChanges() {
    this.activatedRoute.queryParams
      .pipe(
        pairwise()
      )
      .subscribe(([prevParams, currParams]) => {
        if (prevParams.catalog_id !== currParams.catalog_id) {
          this.selected$.value.clear();
          this.selectedElements = [];
        }
      });
  }

  toggleFilters(scrollTop = false, value = null): void {
    if (scrollTop) {
      this.scroller.scrollToPosition([0, 0]);
    }

    this.isFiltersOpen = value === null ? !this.isFiltersOpen : value;

    if (this.isFiltersOpen) {
      this.windowService.open(this.filtersTpl, {
        title: 'Расширенные фильтры',
        windowClass: 'productFilter',
      }).onClose.subscribe(_ => this.isFiltersOpen = false);
    }
  }

  filterSubmit(): void {
    if (this.formFilter.valid) {
      this.toggleFilters(true, false);
      this.onNavigate(this.filter.convertModelFeatures(), null);
    }
  }

  resetFilters(): void {
    this.toggleFilters(true, false);
    this.filter.reset();
    this.cdr.detectChanges();
    this.router.navigate(['.'], {relativeTo: this.route});
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    this.filter.reset();
    this.cdr.detectChanges();
  }

  onFileUnloaded($event) {
    console.warn($event);
    this.filterData();
  }

}
